import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { GroupService } from '../../providers/';

/**
 * Create or join a group
 */
@Component({
    selector: 'group-initial',
    templateUrl: './group-initial.component.html',
    styleUrls: ['./group-initial.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GroupInitialComponent implements OnInit {

    /** `FormControl` for the join ID input element */
    public joinIdControl: UntypedFormControl;
    /** Flag to determine whether the user has entered a join ID for a non-existing group. Is automatically reset */
    public missingGroup: boolean = false;
    /** Flag to determine whether the system is in the process of creating a group */
    public creatingGroup: boolean = false;
    /** Flag to determine whether the user is in the process of joining a group */
    public joiningGroup: boolean = false;

    constructor(private groupService: GroupService) { }

    ngOnInit(): void {
        // Initialize FormControl with validator functions
        this.joinIdControl = new UntypedFormControl('',
            // Validate the input to be 4 digits with a value between 1000 and 9999
            [this.numberPatternValidator(/^[1-9][0-9]{3}$/)]);
    }

    /**
     * Adds the current user to the group given by the join ID entered in the input field tracked by `joinIdControl`
     * @returns A `Promise` which resolves once the user has been added to the group, or if the group does not exist
     */
    public async joinGroup(): Promise<void> {
        this.joiningGroup = true;
        // If errors in form validation, return
        if(this.joinIdControl.errors) return null;
        // Add user to the group with the join ID specified in the input field
        const groupKey = await this.groupService.joinGroup(this.joinIdControl.value);
        // If no group with the specified join ID exists, display error message (for 2 seconds)
        if(!groupKey) {
            this.missingGroup = true;
            window.setTimeout(() => this.missingGroup = false, 2000);
        }
        this.joiningGroup = false;
    }

    /**
     * Creates a new group and adds the current user to it
     * @returns A `Promise` which resolves once the group has been created and the user has been added to it
     */
    public async createGroup(): Promise<void> {
        this.creatingGroup = true;
        // Create a new group
        const group = await this.groupService.createGroup();
        // Add the current user to the group
        await this.groupService.joinGroup(group.key);
        this.creatingGroup = false;
    }

    /**
     * Custom validator function for reactive Angular form:
     * Test a number input using a regular expression.
     * @param numberRe the regular expression the input should match
     * @returns `null` if the input is allowed, otherwise a `ValidationErrors` object
     */
    private numberPatternValidator(numberRe: RegExp): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            // True, if the input matches the provided regex
            const allowed = numberRe.test(control.value?.toString());

            return allowed ? null : { badPattern: { value: control.value } };
        };
    }
}
